// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import {
    ConfigurationType,
    getProductById,
    GetProductByIdApiV2Parameter,
    GetProductVariantChildApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import loadVariantChild from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChild";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import {
    getProductState,
    getVariantChildrenDataView,
    hasEnoughInventory,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState } from "@werner/App/Store/Reducers";

interface Parameter {
    lineId: string;
    variantId?: string;
    productId?: string;
    searchTerm?: string;
    validateProduct?: boolean;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
}

interface Props {
    apiParameter: GetProductByIdApiV2Parameter | GetProductVariantChildApiV2Parameter;
    variantChildren?: ProductModel[] | null;
    product?: ProductModel;
    productInfo?: ProductInfo;
}

type HandlerType = Handler<Parameter & HasOnSuccess, Props>;

export const DispatchBeginSetProduct: HandlerType = props => {
    props.dispatch({
        type: "xQuickOrderLine/ProductSelector/BeginSetProduct" as any,
        lineId: props.parameter.lineId,
    } as any);
};

export const PopulateApiParameter: HandlerType = props => {
    if (props.parameter.variantId && props.parameter.productId) {
        props.apiParameter = {
            id: props.parameter.variantId,
            variantParentId: props.parameter.productId,
            expand: ["detail"],
        };
    } else if (props.parameter.productId) {
        props.apiParameter = {
            id: props.parameter.productId,
            expand: ["detail", "variantTraits"],
        };
    }
};

export const RequestDataFromApi: HandlerType = async props => {
    const {
        apiParameter,
        parameter: { searchTerm },
    } = props;
    if (!apiParameter && !searchTerm) {
        return;
    }

    if (apiParameter) {
        props.product = getProductState(props.getState(), apiParameter.id).value;
        if (
            !props.product ||
            !props.product.detail ||
            (props.product.isVariantParent && !props.product.variantTraits)
        ) {
            if (props.parameter.variantId) {
                props.product = await executeAwaitableHandlerChain(
                    loadVariantChild,
                    props.apiParameter as GetProductVariantChildApiV2Parameter,
                    props,
                );
            } else {
                props.product = await executeAwaitableHandlerChain(loadProduct, props.apiParameter, props);
            }
        }
    } else if (searchTerm) {
        props.product = (
            (await executeAwaitableHandlerChain(
                loadProducts,
                { extendedNames: [searchTerm], expand: ["variantTraits"] },
                props,
            )) as ProductModel[]
        )[0];
    }
};

export const ReplaceProductIfNeeded: HandlerType = async props => {
    const product = props.product;
    if (product && !product.canAddToCart && product.isDiscontinued && product.detail?.replacementProductId) {
        props.product = await getProductById({ id: product.detail.replacementProductId });
    }

    if (!product) {
        return;
    }
    props.productInfo = createFromProduct(product);
};

export const RequestVariantChildrenFromApi: HandlerType = async props => {
    if (!props.product?.variantTraits || props.product.variantTraits.length === 0) {
        return;
    }

    props.variantChildren = getVariantChildrenDataView(props.getState(), props.product.id).value;
    if (!props.variantChildren) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadVariantChildren,
            { productId: props.product.id },
            props,
        );
    }
};

export const DispatchUpdateOptions: HandlerType = props => {
    if (!props.product) {
        return;
    }

    props.dispatch({
        type: "xQuickOrderLine/ProductSelector/UpdateOptions",
        result: props.product,
        lineId: props.parameter.lineId,
    } as any);
};

export const ValidateProduct: HandlerType = ({ parameter, product, productInfo, getState, dispatch }) => {
    if (!parameter.validateProduct || !product || !productInfo) {
        return;
    }

    const productIsConfigurable =
        product.canConfigure ||
        (product.configurationType !== ConfigurationType.None &&
            (product.configurationType !== ConfigurationType.Fixed || parameter.denyFixedConfiguration));

    if (productIsConfigurable) {
        dispatch({
            type: "xQuickOrderLine/ProductSelector/SetErrorType",
            errorType: "productIsConfigurable",
            lineId: parameter.lineId,
        } as any);
    }

    if (product.isVariantParent) {
        dispatch({
            type: "xQuickOrderLine/ProductSelector/OpenVariantModal",
            productId: product.id,
            lineId: parameter.lineId,
        } as any);

        return false;
    }

    if (!parameter.skipInventoryValidation && !hasEnoughInventory(getState(), { product, productInfo })) {
        dispatch({
            type: "xQuickOrderLine/ProductSelector/SetErrorType",
            errorType: "productIsUnavailable",
            lineId: parameter.lineId,
        } as any);
    }

    if (!product.canAddToCart && !productIsConfigurable) {
        dispatch({
            type: "xQuickOrderLine/ProductSelector/SetErrorType",
            errorType: "productIsUnavailable",
            lineId: parameter.lineId,
        } as any);
    }
};

export const DispatchCompleteSetProduct: HandlerType = props => {
    props.dispatch({
        type: "xQuickOrderLine/ProductSelector/CompleteSetProduct",
        productInfo: props.productInfo,
        lineId: props.parameter.lineId,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = async props => {
    await waitFor(
        () =>
            !!(props.getState() as CustomAppState).xQuickOrderLine.quickOrderLines[props.parameter.lineId]
                .selectedProductInfo,
    );

    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginSetProduct,
    PopulateApiParameter,
    RequestDataFromApi,
    ReplaceProductIfNeeded,
    RequestVariantChildrenFromApi,
    DispatchUpdateOptions,
    ValidateProduct,
    DispatchCompleteSetProduct,
    ExecuteOnSuccessCallback,
];

const setProduct = createHandlerChainRunner(chain, "SetMiniQuickOrderProduct");
export default setProduct;
